import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useTagStore from '../store/tagStore';
import TagDialog from '../components/tags/TagDialog';

const TagManagementPage = () => {
  const { t } = useTranslation();
  const { tags, loading, error, fetchTags, deleteTag } = useTagStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleAddTag = () => {
    setSelectedTag(null);
    setDialogOpen(true);
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setDialogOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    if (window.confirm(t('tags.confirmDelete', 'Are you sure you want to delete this tag?'))) {
      try {
        await deleteTag(tagId);
      } catch (err) {
        console.error('Failed to delete tag:', err);
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTag(null);
  };

  // Filter tags based on search query
  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddTag}
        sx={{
          mb: 3,
          backgroundColor: '#FFD700',
          color: '#000',
          '&:hover': {
            backgroundColor: '#FFC700',
          }
        }}
      >
        {t('tags.addNew', 'Add new tag')}
      </Button>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('tags.search', 'Search (Or-search with "," , And-search with "&")')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t('tags.searchHelp', '(Or-search with "," , And-search with "&")')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" gutterBottom>
            {t('tags.showing', 'Showing')} {filteredTags.length} {t('tags.of', 'of')} {tags.length}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {filteredTags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                icon={<span>{tag.icon}</span>}
                onClick={() => handleEditTag(tag)}
                onDelete={() => handleDeleteTag(tag.id)}
                sx={{
                  backgroundColor: tag.color,
                  color: '#000',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: '#000',
                    '&:hover': {
                      color: '#666',
                    }
                  },
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  }
                }}
              />
            ))}
          </Box>
          {filteredTags.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {searchQuery
                ? t('tags.noResults', 'No tags found matching your search')
                : t('tags.noTags', 'No tags yet. Click "Add new tag" to create one.')}
            </Typography>
          )}
        </>
      )}

      <TagDialog
        open={dialogOpen}
        tag={selectedTag}
        onClose={handleDialogClose}
      />
    </Container>
  );
};

export default TagManagementPage;
